import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboardingStore } from '../../store/useOnboardingStore';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface SecureModalProps {
  children: React.ReactNode;
  preventClose?: boolean;
}

export const SecureModal: React.FC<SecureModalProps> = ({ children, preventClose = true }) => {
  const { isOpen, closeModal } = useOnboardingStore();

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && preventClose) {
      e.preventDefault();
      toast.error('Please complete the onboarding process');
    }
  }, [preventClose]);

  // Handle click outside
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (preventClose) {
      e.preventDefault();
      toast.error('Please complete the onboarding process');
    } else if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  useEffect(() => {
    // Add event listener for escape key
    document.addEventListener('keydown', handleKeyDown);
    
    // Add/remove blur class on body when modal opens/closes
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.style.filter = isOpen ? 'blur(8px)' : 'none';
    }

    // Add CSS to prevent modal closing via dev tools
    const style = document.createElement('style');
    style.innerHTML = `
      .secure-modal { 
        visibility: visible !important;
        opacity: 1 !important;
        display: flex !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (mainContent) {
        mainContent.style.filter = 'none';
      }
      document.head.removeChild(style);
    };
  }, [handleKeyDown, isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="secure-modal fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={handleOverlayClick}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative bg-white rounded-2xl shadow-xl max-w-[90%] max-h-[90vh] overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};